import { FitnessVillageCopy } from './fitnessVillageCopy';

/**
 * The Arabic twin of `FITNESS_VILLAGE_COPY_EN` — same keys, same functions, typed by
 * `FitnessVillageCopy` so a key added in English fails the build until it is added here too.
 * Comments on what each string is for live on the English file.
 *
 * UNVERIFIED — a first translation by the product team. DFC owns the final Arabic wording (and
 * the legal lines in `signup` especially) and should review the whole file before launch.
 *
 * Arabic numerals stay Western (3, 60, 18:30) as on DFC's own Arabic site. Text that may hold a
 * left-to-right value (an email) wraps it in first-strong isolates (U+2068 … U+2069) so the
 * sentence around it keeps its order.
 */

const pluralRules = new Intl.PluralRules('ar');

/** Arabic counts agree with the number in six forms: one, two, few (3–10), many (11–99), … */
const plural = (
  count: number,
  forms: { zero?: string; one: string; two: string; few: string; many: string; other: string }
): string => {
  const form = pluralRules.select(count) as keyof typeof forms;
  return (forms[form] ?? forms.other).replace('{n}', String(count));
};

const isolate = (value: string) => `⁨${value}⁩`;

export const FITNESS_VILLAGE_COPY_AR: FitnessVillageCopy = {
  home: {
    dateline: '31 أكتوبر – 29 نوفمبر 2026',
    heroTitle: { lead: 'تحدي دبي', tail: 'للياقة' },
    heroLead: 'إلى أين',
    heroAccent: 'تتحرك اليوم؟',
    heroArtworkAlt: '30 دقيقة، 30 يوماً',
    intro: 'ثلاث قرى للياقة 30×30 في أنحاء المدينة. الحصص مجانية ومفتوحة للجميع، فقط احضر.',
    chooseVillage: 'اختر قرية',
    classesAction: {
      label: 'الحصص',
      sub: 'لا حاجة للحجز',
      aria: (village: string) => `تصفّح الحصص في ${village}`,
    },
    courtsAction: {
      label: 'الملاعب',
      sub: 'احجز موعداً',
      aria: (village: string) => `احجز ملعباً في ${village}`,
    },
    expandVillage: (village: string) => `عرض خيارات ${village}`,
    collapseVillage: (village: string) => `إخفاء خيارات ${village}`,
    emptyVillages: 'يُفتح باب الحجز في القرى قريباً.',
    stats: {
      title: 'التحدي بالأرقام',
      footnote: 'الدخول مجاني للجميع. تحتاج إلى حساب فقط لحجز ملعب.',
    },
    lockups: {
      title: 'قرى اللياقة',
      alt: (village: string) => `قرية اللياقة في ${village}`,
    },
  },

  homeStats: [
    { label: 'قرى للياقة', value: '3' },
    { label: 'منطقة في قرى اللياقة 30×30', value: '19' },
    { label: 'يوماً من الحركة المجانية', value: '30' },
  ],

  page: {
    allVillages: 'جميع القرى',
    switchVillage: 'تغيير القرية',
    classesLead: 'الفعاليات',
    courtsLead: 'احجز ملعباً في',
    tabs: { classes: 'الحصص', courts: 'الملاعب', ariaLabel: 'الحصص أو الملاعب' },
    zones: 'المناطق',
    sports: 'الملاعب',
    close: 'إغلاق',
    alsoOnToday: 'أيضاً اليوم',
    alsoBookable: 'متاح للحجز أيضاً',
    seeAllZones: (count: number) => `عرض جميع المناطق (${count})`,
    seeAllSports: (count: number) => `عرض جميع الرياضات (${count})`,
    timetable: 'الجدول',
    yourDay: 'يومك',
    happeningNow: 'يجري الآن',
    howLong: 'كم المدة؟',
    sessionLength: 'مدة الجلسة',
    start: 'البداية',
    bookCourt: 'احجز',
    classesIntro: 'حصص مجانية ومفتوحة للجميع دون حجز، فقط احضر. تُطبَّق حدود للسعة في كل منطقة.',
    courtsIntro:
      'احجز ملعباً لمدة 60 دقيقة. ملعب واحد لكل شخص في اليوم، وحتى 3 ملاعب في الأسبوع. نرغب في أن يستمتع أكبر عدد ممكن من المشاركين بالمرافق، لذا نرجو مراعاة الآخرين!',
    courtHours: (hours: string) => `حجز الملاعب: ${hours}`,
    classesNote: 'الحصص حسب التوفر وقد تكتمل.',
    chooseDay: 'اختر يوماً',
    today: 'اليوم',
    previousWeek: 'الأسبوع السابق',
    nextWeek: 'الأسبوع التالي',
    emptyClasses: 'لا توجد حصص مجدولة هنا اليوم.',
    dropInLabel: 'حضور مباشر',
    dropInFallback: 'احضر خلال ساعات العمل، لا حاجة للحجز.',
    emptySlots: 'لا توجد مواعيد متاحة في هذا اليوم.',
    loadError: 'تعذّر تحميل هذه القرية. حاول مرة أخرى.',
    retry: 'حاول مرة أخرى',
    mapComingSoon: 'خريطة القرية قريباً.',
    villageTitle: (village: string) => `قرية اللياقة في ${village}`,
    periods: { Morning: 'الصباح', Afternoon: 'بعد الظهر', Evening: 'المساء' },
    courtsCount: (count: number) =>
      plural(count, {
        one: 'ملعب واحد',
        two: 'ملعبان',
        few: '{n} ملاعب',
        many: '{n} ملعباً',
        other: '{n} ملعب',
      }),
    changeSelection: (label: string) => `تغيير الاختيار. الاختيار الحالي: ${label}`,
    alreadyBooked: (time: string) => `${time} — محجوز`,
    periodSlots: (period: string) => `مواعيد ${period}`,
    unknownVillage: 'تعذّر العثور على هذه القرية.',
    free: 'مجاني',
    classDetails: {
      minutes: (count: number) => `${count} دقيقة`,
      capacity: (count: number) => `السعة ${count}`,
      spotsLeft: (left: number, total: number) => `${left} من ${total} أماكن متاحة`,
      full: 'مكتمل',
      dropIn: 'حضور مجاني بدون حجز. تُطبَّق حدود السعة.',
      noDescription: 'المزيد عن هذه الحصة قريباً.',
    },
    reserveError: 'تعذّر حجز هذا الموعد. جرّب وقتاً آخر.',
  },

  auth: {
    eyebrow: 'تحدي دبي للياقة',
    signIn: {
      tab: 'تسجيل الدخول',
      title: 'مرحباً بعودتك',
      body: 'سجّل الدخول لعرض حجوزاتك.',
      cta: 'تسجيل الدخول',
    },
    signUp: {
      tab: 'إنشاء حساب',
      title: 'إنشاء حساب',
      body: 'حساب واحد لجميع القرى، واحتفظ بحجوزات الملاعب في مكان واحد.',
      cta: 'إنشاء حساب',
    },
    overlayLabel: 'تسجيل الدخول أو إنشاء حساب',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'you@email.com',
    password: 'كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    forgotPassword: 'نسيت كلمة المرور؟',
    resetSent: 'تم إرسال رسالة إعادة التعيين',
    pending: 'لحظة واحدة…',
    orContinueWith: 'أو تابع باستخدام',
    google: 'Google',
    apple: 'Apple',
    close: 'إغلاق',
  },

  header: {
    logoAlt: 'تحدي دبي للياقة',
    home: 'الصفحة الرئيسية لتحدي دبي للياقة',
    mainNav: 'القائمة الرئيسية',
    signIn: 'تسجيل الدخول',
    openMenu: 'فتح القائمة',
    account: 'الحساب',
    quickCheckIn: 'رمز QR لتسجيل الوصول السريع',
    cart: 'السلة',
    darkMode: 'الوضع الداكن',
    qrModal: {
      title: 'تسجيل الوصول السريع',
      subtitle: 'امسح رمز QR لتسجيل الوصول السريع',
      viewProfile: 'عرض ملفي الشخصي',
      close: 'إغلاق',
    },
    languageSwitch: { label: 'English', short: 'EN', lang: 'en', aria: 'التبديل إلى الإنجليزية' },
    nav: {
      home: 'الرئيسية',
      schedule: 'الحصص',
      bookings: 'الملاعب',
    },
    menu: 'القائمة',
    closeMenu: 'إغلاق القائمة',
    myProfile: 'ملفي الشخصي',
    logOut: 'تسجيل الخروج',
    menuFootnote: 'تحدي دبي للياقة · 31 أكتوبر – 29 نوفمبر 2026',
    cartHold: 'أكمل حجزك خلال',
    cartHoldAria: (minutes: number, seconds: number) =>
      `أكمل حجزك خلال ${plural(minutes, {
        zero: '0 دقيقة',
        one: 'دقيقة واحدة',
        two: 'دقيقتين',
        few: '{n} دقائق',
        many: '{n} دقيقة',
        other: '{n} دقيقة',
      })} و${plural(seconds, {
        zero: '0 ثانية',
        one: 'ثانية واحدة',
        two: 'ثانيتين',
        few: '{n} ثوانٍ',
        many: '{n} ثانية',
        other: '{n} ثانية',
      })}`,
  },

  checkout: {
    backToVillages: 'جميع القرى',
    heroLead: 'تفاصيل',
    checkoutHeroLead: 'تأكيد',
    heroAccent: 'حجزك',
    cartHeroAccent: 'سلتك',
    addOnsHeroLead: 'أضف',
    addOnsHeroAccent: 'إضافات',
    bookings: 'حجوزاتك',
    addOns: 'الإضافات',
    optional: 'اختياري',
    discounts: 'الخصومات',
    details: 'بياناتك',
    summary: 'الملخص',
    confirm: 'التأكيد',
    itemCount: (count: number) =>
      plural(count, {
        one: 'عنصر واحد',
        two: 'عنصران',
        few: '{n} عناصر',
        many: '{n} عنصراً',
        other: '{n} عنصر',
      }),
    remove: 'إزالة',
    cancel: 'إلغاء',
    removeAria: (service: string, time: string) => `إزالة ${service} الساعة ${time}`,
    cancelAria: (service: string, time: string) => `إلغاء ${service} الساعة ${time}`,
    minutes: (count: number) => `${count} دقيقة`,
    quantity: (count: number) => `×${count}`,
    addOnsCount: (count: number) => `الإضافات (${count})`,
    invoice: 'إصدار الفاتورة',
    share: 'مشاركة',
    free: 'مجاني',
    emptyCart: 'سلتك فارغة.',
    emptyCartAction: 'تصفّح القرى',
    handoffTitle: 'تم تأكيد الحجز',
    handoffBody: 'جارٍ فتح تأكيد حجزك…',
    noBookings: 'لا توجد حجوزات.',
    subtotal: 'المجموع الفرعي',
    discount: 'الخصم',
    vat: 'ضريبة القيمة المضافة',
    total: 'الإجمالي',
    paid: 'المدفوع',
    detailsIntro: 'سنرسل تفاصيل حجزك إلى هذا البريد الإلكتروني.',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    email: 'البريد الإلكتروني',
    phone: 'رقم الهاتف المتحرك',
    country: 'رمز الدولة',
    agreeLead: 'أوافق على',
    privacy: 'سياسة الخصوصية',
    terms: 'الشروط والأحكام',
    cancellationPolicy: 'سياسة الإلغاء',
    and: 'و',
    listSeparator: '، ',
    sentenceEnd: '.',
    cancellationNote: (hours: number) =>
      `يمكن إلغاء الحجوزات عبر الإنترنت حتى ${hours} ساعة قبل موعد بدئها.`,
    yes: 'نعم',
    no: 'لا',
    processing: 'لحظة واحدة…',
    actions: {
      book: 'احجز الآن',
      pay: 'المتابعة إلى الدفع',
      package: 'احجز باستخدام الباقة',
    },
    signInInstead: 'لديك حساب؟ سجّل الدخول',
    guestInstead: 'المتابعة كضيف',
    continueToCheckout: 'المتابعة لإتمام الحجز',
    continue: 'متابعة',
    addOnsNote: 'أضف معدات إلى حجز الملعب، أو تابع بدونها.',
    noAddOns: 'لا توجد إضافات لهذا الحجز.',
    addOnPer: (price: string) => `${price} للقطعة`,
    decrease: (name: string) => `إنقاص ${name} بمقدار واحد`,
    increase: (name: string) => `زيادة ${name} بمقدار واحد`,
  },

  confirmation: {
    eyebrow: 'تم حجزك',
    heroLead: 'تم تأكيد',
    heroAccent: 'الحجز',
    sentTo: (email: string) => `أرسلنا التفاصيل إلى ${isolate(email)}.`,
    sent: 'أرسلنا التفاصيل إلى بريدك الإلكتروني.',
    booked: 'تم الحجز',
    backHome: 'العودة إلى القرى',
  },

  profile: {
    tabs: {
      ariaLabel: 'أقسام الملف الشخصي',
      profile: 'الملف الشخصي',
      upcoming: 'القادمة',
      previous: 'السابقة',
    },
    avatarFallback: '?',
    details: 'بياناتك',
    fullName: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    mobile: 'الهاتف المتحرك',
    dateOfBirth: 'تاريخ الميلاد',
    nationality: 'الجنسية',
    empty: '—',
    checkIn: 'تسجيل الوصول السريع',
    checkInBody: 'أظهر هذا الرمز عند مدخل القرية.',
    wallet: 'أضف إلى محفظتك',
    memberships: 'العضويات',
    credits: 'القسائم والأرصدة',
    noMemberships: 'لا توجد عضويات بعد.',
    noCredits: 'لا توجد قسائم أو أرصدة بعد.',
    active: 'نشط',
    inactive: 'غير نشط',
    code: 'الرمز',
    remaining: 'المتبقي',
    value: 'القيمة',
    redeemed: 'المُستخدَم',
    price: 'السعر',
    purchased: 'تاريخ الشراء',
    starts: 'يبدأ في',
    validUntil: 'صالح حتى',
    creditFallback: 'رصيد من حجز مُلغى',
    invoice: 'إصدار الفاتورة',
    noUpcoming: 'لا توجد حجوزات قادمة.',
    noPrevious: 'لا توجد حجوزات سابقة.',
    browseVillages: 'تصفّح القرى',
    orderPlaced: (date: string) => `تم الطلب في ${date}`,
    order: 'الطلب',
    downloadInvoice: 'تنزيل الفاتورة',
    pageRange: (from: number, to: number, total: number) => `${from}–${to} من ${total}`,
    previousPage: 'الصفحة السابقة',
    nextPage: 'الصفحة التالية',
    account: 'الحساب',
    logOut: 'تسجيل الخروج',
    deleteAccount: 'حذف الحساب',
    deleteTitle: 'هل تريد حذف حسابك؟',
    deleteBody: 'سيؤدي هذا إلى حذف حسابك نهائياً وكل ما يرتبط به، بما في ذلك حجوزاتك.',
    deleteConfirm: 'حذف الحساب',
    cancel: 'إلغاء',
  },

  signup: {
    eyebrow: 'تحدي دبي للياقة',
    titleEmail: 'أنشئ حسابك',
    titleSocial: 'أكمل ملفك الشخصي',
    bodyEmail: 'اختر كلمة مرور لإكمال إنشاء حسابك.',
    bodySocial: 'بعض التفاصيل وتصبح جاهزاً.',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    password: 'كلمة المرور',
    passwordConfirm: 'أعد إدخال كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    /** UNVERIFIED — DFC's legal team to supply the official Arabic consent wording. */
    consentLead: 'بإنشاء حساب، أؤكد موافقتي على',
    privacyNotice: 'إشعار الخصوصية',
    and: 'و',
    termsOfUse: 'شروط الاستخدام',
    submit: 'إنشاء حساب',
    pending: 'لحظة واحدة…',
  },

  cancellation: {
    eyebrow: 'إدارة الحجز',
    heroLead: 'إلغاء',
    heroAccent: 'الحجز',
    intro: 'ألغِ الحجز الذي لم تعد بحاجة إليه. تبقى الحجوزات الأخرى في هذا الطلب كما هي.',
    bookings: 'حجزك',
    backHome: 'العودة إلى القرى',
    myBookings: 'حجوزاتي',
    note: (date: string) => `تم إلغاء حجزك بتاريخ ${date}.`,
    refundNote: (date: string, price: string) =>
      `تم إلغاء حجزك بتاريخ ${date} بقيمة ${isolate(price)}، وسيُعاد المبلغ إلى وسيلة الدفع الأصلية. يُرجى الانتظار حتى 14 يوماً لمعالجة الاسترداد.`,
  },
  loader: {
    label: 'جارٍ تحميل قرى اللياقة',
  },
};

/**
 * Arabic for English messages that live in shared code — toasts, form validation, page titles —
 * keyed by the exact English text. Shared code keeps its English and wraps it in
 * `localizeMessage()` (src/Utils/dfcMessages.ts); an English line that changes without its key
 * changing here simply shows in English. Backend messages are not covered.
 */
export const FITNESS_VILLAGE_MESSAGES_AR: Record<string, string> = {
  // Cart and checkout toasts
  'Booking slot is added to the cart': 'تمت إضافة الموعد إلى السلة',
  'Booking slot is removed from the cart': 'تمت إزالة الموعد من السلة',
  'Addon is removed from the cart': 'تمت إزالة الإضافة من السلة',
  'Error while adding the item to the cart': 'حدث خطأ أثناء إضافة العنصر إلى السلة',
  'An error has occurred while processing your booking payment': 'حدث خطأ أثناء معالجة حجزك',
  'You can book one court per day': 'يمكنك حجز ملعب واحد فقط في اليوم',
  'You can book up to 3 courts per week': 'يمكنك حجز 3 ملاعب كحد أقصى في الأسبوع',
  'Failed to cancel booking': 'تعذّر إلغاء الحجز',

  // Sign in, create account, account
  'Email already exists. Please log in.': 'هذا البريد الإلكتروني مسجّل مسبقاً. يرجى تسجيل الدخول.',
  'Incorrect email or password': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
  'Please verify your email to continue': 'يرجى تأكيد بريدك الإلكتروني للمتابعة',
  'Please Complete Your Profile': 'يرجى إكمال ملفك الشخصي',
  'Email not found': 'البريد الإلكتروني غير مسجّل',
  'Please try to sign up again': 'يرجى محاولة التسجيل مرة أخرى',
  'Check your email to reset your password': 'تحقق من بريدك الإلكتروني لإعادة تعيين كلمة المرور',
  'Error while logout': 'حدث خطأ أثناء تسجيل الخروج',
  'Account Deleted Successfully': 'تم حذف الحساب بنجاح',
  'Error while Delete Account': 'حدث خطأ أثناء حذف الحساب',
  'You should Login first': 'يجب تسجيل الدخول أولاً',
  'Failed to complete profile': 'تعذّر إكمال الملف الشخصي',
  'Profile image upload failed. Continuing with registration...':
    'تعذّر رفع صورة الملف الشخصي. جارٍ متابعة التسجيل…',

  // Form validation (src/Utils/validate.tsx)
  'Invalid Email Address': 'عنوان البريد الإلكتروني غير صالح',
  'Email is Required': 'البريد الإلكتروني مطلوب',
  'Password is required': 'كلمة المرور مطلوبة',
  'Password must be at least 8 characters': 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
  'Password must contain at least one letter': 'يجب أن تحتوي كلمة المرور على حرف واحد على الأقل',
  'Password must contain at least one lowercase letter':
    'يجب أن تحتوي كلمة المرور على حرف صغير واحد على الأقل',
  'Password must contain at least one uppercase letter':
    'يجب أن تحتوي كلمة المرور على حرف كبير واحد على الأقل',
  'Password must contain at least one number': 'يجب أن تحتوي كلمة المرور على رقم واحد على الأقل',
  'Password must contain at least one special character':
    'يجب أن تحتوي كلمة المرور على رمز خاص واحد على الأقل',
  'Password confirmation is required': 'تأكيد كلمة المرور مطلوب',
  "Passwords don't match": 'كلمتا المرور غير متطابقتين',
  'First Name is Required': 'الاسم الأول مطلوب',
  'Last Name is Required': 'اسم العائلة مطلوب',
  'Privacy is required': 'يرجى الموافقة على إشعار الخصوصية وشروط الاستخدام',

  // Wallet passes
  'Failed to add to Google Wallet': 'تعذّرت الإضافة إلى Google Wallet',
  'Failed to add to Apple Wallet': 'تعذّرت الإضافة إلى Apple Wallet',

  // Page titles (App.tsx route titles, shown in the browser tab after the company name)
  Home: 'الرئيسية',
  'Fitness Village': 'قرية اللياقة',
  Cart: 'السلة',
  Checkout: 'إتمام الحجز',
  'Add-ons': 'الإضافات',
  Confirmation: 'تأكيد الحجز',
  Cancellation: 'إلغاء الحجز',
  'My-profile': 'ملفي الشخصي',
  Register: 'تسجيل الدخول',
};
